import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { acceptOwnerRoomHold, fetchOwnerRoomHolds } from "../services/hotelOwnerAccessService";
import { formatDateDMY } from "../utils/dateFormat";
import { AcceptanceProofFields, useAcceptanceProof } from "../components/AcceptanceProof";

/**
 * Rooms Iotrips has asked this hotel to keep aside. The hotel can accept a hold here; only Iotrips
 * can release one, so there is no release button on this side.
 */
export default function HotelOwnerRoomHolds({ portalKey, token, onUnauthorized }) {
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acceptingId, setAcceptingId] = useState(null);
  // The hold whose Accept was pressed, kept until the accepter gives their name and location.
  const [confirmHold, setConfirmHold] = useState(null);
  const proofState = useAcceptanceProof(!!confirmHold);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHolds(await fetchOwnerRoomHolds(portalKey, token));
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) onUnauthorized?.();
      // A failed lookup just hides the section; availability below still shows held rooms.
      setHolds([]);
    } finally {
      setLoading(false);
    }
  }, [portalKey, token, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const accept = async () => {
    const hold = confirmHold;
    if (!hold || !proofState.proof) return;
    setAcceptingId(hold.id);
    try {
      await acceptOwnerRoomHold(portalKey, token, hold.id, proofState.proof);
      proofState.markUsed();
      setConfirmHold(null);
      toast.success("Thank you - hold accepted");
      await load();
    } catch (error) {
      if (error?.response?.status === 401) onUnauthorized?.();
      toast.error(error?.response?.data?.message || "Could not accept the hold");
    } finally {
      setAcceptingId(null);
    }
  };

  if (!loading && holds.length === 0) return null;
  const awaiting = holds.filter((hold) => hold.hotelAgreement === "AWAITING_HOTEL").length;

  return (
    <div className="container-fluid pt-3">
      <Card className="shadow-sm border-primary">
        <Card.Header className="d-flex flex-wrap align-items-center gap-2">
          <span className="fw-semibold">
            <i className="bi bi-lock-fill me-1 text-primary" aria-hidden="true" />
            Rooms held for Iotrips
          </span>
          {awaiting > 0 && <Badge bg="warning" text="dark">{awaiting} waiting for your OK</Badge>}
        </Card.Header>
        <Card.Body className="py-2">
          <div className="small text-muted mb-2">
            Please keep these rooms for Iotrips and do not give them to other guests. Iotrips will tell you
            when a room is no longer needed.
          </div>
          {loading && <Spinner animation="border" size="sm" />}
          <div className="d-flex flex-column gap-2">
            {holds.map((hold) => {
              const unused = (hold.nights || []).reduce((least, night) => Math.min(least, night.unused), hold.units);
              return (
                <div key={hold.id} className="border rounded p-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <div className="fw-semibold">
                      {hold.categoryName} - {hold.units} room{hold.units === 1 ? "" : "s"}
                      {hold.hotelCode ? <span className="text-muted fw-normal"> · {hold.hotelCode}</span> : null}
                    </div>
                    <div className="small">
                      {formatDateDMY(hold.startDate)} to {formatDateDMY(hold.endDate)} (check-out)
                    </div>
                    {unused < hold.units && (
                      <div className="small text-muted">
                        Some of these rooms are already booked by Iotrips guests - see bookings above.
                      </div>
                    )}
                    {hold.note && <div className="small text-muted fst-italic">{hold.note}</div>}
                  </div>
                  {hold.hotelAgreement === "AWAITING_HOTEL" ? (
                    <Button size="sm" variant="success" disabled={acceptingId === hold.id} onClick={() => setConfirmHold(hold)}>
                      {acceptingId === hold.id ? <Spinner animation="border" size="sm" /> : "Accept"}
                    </Button>
                  ) : (
                    <div className="text-end">
                      <Badge bg="success">Accepted</Badge>
                      {hold.hotelAcceptedByName && (
                        <div className="small fw-semibold text-success mt-1">by {hold.hotelAcceptedByName}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      <Modal show={!!confirmHold} onHide={() => !acceptingId && setConfirmHold(null)} centered>
        <Modal.Header closeButton={!acceptingId}>
          <Modal.Title>Accept room hold?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmHold && (
            <>
              <div className="fw-semibold">
                {confirmHold.categoryName} - {confirmHold.units} room{confirmHold.units === 1 ? "" : "s"}
              </div>
              <div className="small mb-3">
                {formatDateDMY(confirmHold.startDate)} to {formatDateDMY(confirmHold.endDate)} (check-out)
              </div>
            </>
          )}
          <Alert variant="warning" className="mb-3 small">
            Please keep these rooms only for Iotrips until Iotrips releases them.
          </Alert>
          <AcceptanceProofFields state={proofState} disabled={!!acceptingId} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={!!acceptingId} onClick={() => setConfirmHold(null)}>
            Cancel
          </Button>
          <Button variant="success" disabled={!!acceptingId || !proofState.proof} onClick={accept}>
            {acceptingId ? <Spinner animation="border" size="sm" /> : "Accept hold"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
